import LocationOnIcon from '@mui/icons-material/LocationOn';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { LoadScript, GoogleMap, Marker } from '@react-google-maps/api';
import { Loader } from '@googlemaps/js-api-loader';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslate } from 'react-admin';
import { useFormContext, useWatch, type UseFormSetValue } from 'react-hook-form';
import { ADDRESS_TEXT, CITY, COUNTRY, GEOCODE_APIKEY, GOOGLE_MAPS_LIBRARIES, POSTAL_CODE } from '@/utils/CONST';
import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import PlacesAutocomplete from '@/components/common/places-autocomplete/places-autocomplete';

const formatCoordinate = (coord: number | null | undefined): string => {
    if (coord === null || coord === undefined || isNaN(coord)) {
        return String(coord ?? '');
    }
    return coord.toFixed(4);
};

const formatCoordinateNumber = (coord: number | null | undefined): number => {
    if (coord === null || coord === undefined || isNaN(coord)) {
        return coord ?? 0;
    }
    return parseFloat(coord.toFixed(4));
};

type GeocodeAddressComponent = { long_name: string; types: string[] };

/** Aplica resultado de geocodificación a los campos del formulario (mismo criterio que el mapa). */
const applyGeocodeToForm = (
    setValue: UseFormSetValue<Record<string, unknown>>,
    address_components: GeocodeAddressComponent[],
    formatted_address: string,
    lat: number,
    lng: number,
) => {
    const formattedLat = formatCoordinateNumber(lat);
    const formattedLng = formatCoordinateNumber(lng);
    const locationText = `Lat: ${formatCoordinate(formattedLat)}, Lng: ${formatCoordinate(formattedLng)}`;

    setValue('latitude', formattedLat, { shouldDirty: true });
    setValue('longitude', formattedLng, { shouldDirty: true });
    setValue('location', locationText, { shouldDirty: true, shouldValidate: true });

    setValue(
        POSTAL_CODE,
        address_components?.find(({ types }) => types[0] === 'postal_code')?.long_name ?? '',
        { shouldDirty: true, shouldValidate: true },
    );
    setValue(
        COUNTRY,
        address_components?.find(({ types }) => types[0] === 'country')?.long_name ?? '',
        { shouldDirty: true, shouldValidate: true },
    );
    setValue(ADDRESS_TEXT, formatted_address ?? '', { shouldDirty: true, shouldValidate: true });
    setValue(
        CITY,
        address_components?.find(({ types }) => types[0] === 'locality')?.long_name ?? '',
        { shouldDirty: true, shouldValidate: true },
    );
};

interface LocationFieldProps {
    readOnly?: boolean;
}

export const LocationField = ({}: LocationFieldProps) => {
    const translate = useTranslate();
    const { setValue } = useFormContext();
    const [dialogMapState, setDialogMapState] = useState(false);
    const locationValue = useWatch({ name: 'location' });
    const latitude = useWatch({ name: 'latitude' });
    const longitude = useWatch({ name: 'longitude' });
    const addressText = useWatch({ name: ADDRESS_TEXT });

    /**
     * Si ya hay coordenadas pero la dirección sigue vacía (p. ej. compañía solo con lat/lng),
     * geocodificar con la Maps JavaScript API (mismo mecanismo que el mapa).
     * No usar el endpoint REST geocode/json desde el navegador: suele fallar por CORS.
     */
    useEffect(() => {
        if (!GEOCODE_APIKEY) return;

        const lat = typeof latitude === 'string' ? parseFloat(latitude) : Number(latitude);
        const lng = typeof longitude === 'string' ? parseFloat(longitude) : Number(longitude);
        if (isNaN(lat) || isNaN(lng)) return;

        const existingAddress =
            typeof addressText === 'string' ? addressText.trim() : String(addressText ?? '').trim();
        if (existingAddress) return;

        let cancelled = false;
        const loader = new Loader({
            apiKey: GEOCODE_APIKEY,
            version: 'weekly',
            libraries: GOOGLE_MAPS_LIBRARIES,
        });

        loader
            .load()
            .then(() => {
                if (cancelled) return;
                const geocoder = new google.maps.Geocoder();
                geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                    if (cancelled || status !== 'OK' || !results?.[0]) return;
                    const { address_components, formatted_address } = results[0];
                    applyGeocodeToForm(
                        setValue,
                        (address_components || []) as GeocodeAddressComponent[],
                        formatted_address ?? '',
                        lat,
                        lng,
                    );
                });
            })
            .catch(() => {
                /* sin UI: el usuario puede abrir el mapa para reintentar */
            });

        return () => {
            cancelled = true;
        };
    }, [latitude, longitude, addressText, setValue]);

    // Priorizar locationValue si existe, sino usar coordenadas si están disponibles
    const displayText = 
        locationValue && locationValue.trim() !== ''
            ? locationValue
            : (latitude && longitude && !isNaN(latitude) && !isNaN(longitude))
                ? `Lat: ${formatCoordinate(latitude)}, Lng: ${formatCoordinate(longitude)}`
                : translate('site.form.inputMap');

    return (
        <>
            <Box sx={{ width: '100%' }}>
                <Box
                    onClick={() => setDialogMapState(true)}
                    sx={{
                        cursor: 'pointer',
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                        backgroundColor: (theme) =>
                            theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            backgroundColor: (theme) =>
                                theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[800],
                            borderColor: (theme) => theme.palette.primary.main,
                            boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
                        },
                        '&:active': {
                            transform: 'scale(0.98)',
                        },
                    }}
                    data-test-sc={'LatitudeLongitude'}
                >
                    <LocationOnIcon
                        sx={{
                            color: 'primary.main',
                            fontSize: 24,
                            flexShrink: 0,
                        }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', maxWidth: '100%' }}>
                        <Typography
                            variant='caption'
                            sx={{
                                display: 'block',
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                                mb: 0.25,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                            }}
                        >
                            {`${translate('site.form.latitude')}, ${translate('site.form.longitude')}`}
                        </Typography>
                        <Typography
                            variant='body2'
                            sx={{
                                color: 'text.primary',
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                            }}
                        >
                            {displayText}
                        </Typography>
                    </Box>
                </Box>
                <Typography
                    variant='caption'
                    sx={{
                        display: 'block',
                        color: 'primary.main',
                        fontSize: '0.7rem',
                        mt: 0.5,
                        ml: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {translate('site.form.inputMap')}
                </Typography>
            </Box>
            <CustomMapDialog {...{ dialogMapState, setDialogMapState }} />
        </>
    );
};

interface CustomMapProps {
    setDialogMapState: any;
    dialogMapState: boolean;
}

const CustomMapDialog = ({ setDialogMapState, dialogMapState }: CustomMapProps) => {
    const translate = useTranslate();
    const { setValue, getValues } = useFormContext();
    const [viewState, setViewState] = useState<{ latitude: number; longitude: number; zoom: number }>({
        latitude: isNaN(getValues('latitude')) || !getValues('latitude') ? 46.71109 : Number(getValues('latitude')),
        longitude:
            isNaN(getValues('longitude')) || !getValues('longitude') ? 1.7191036 : Number(getValues('longitude')),
        zoom: 16,
    });

    const [addresses, setAddresses] = useState<{ address_components: any; formatted_address: string }>({
        address_components: [],
        formatted_address: '',
    });

    // Referencias para el mapa y el marcador
    const mapRef = useRef<google.maps.Map | null>(null);
    const geocoderRef = useRef<google.maps.Geocoder | null>(null);
    const isInitialLoadRef = useRef(true);
    const isDraggingRef = useRef(false);

    function handleAddressChange() {
        const { address_components, formatted_address } = addresses;

        setValue(
            POSTAL_CODE,
            address_components?.find(({ types }: { types: string }) => types[0] === 'postal_code')?.long_name ?? '',
            { shouldDirty: true, shouldValidate: true },
        );
        setValue(
            COUNTRY,
            address_components?.find(({ types }: { types: string }) => types[0] === 'country')?.long_name ?? '',
            { shouldDirty: true, shouldValidate: true },
        );
        setValue(ADDRESS_TEXT, formatted_address ?? '', { shouldDirty: true, shouldValidate: true });
        setValue(
            CITY,
            address_components?.find(({ types }: { types: string }) => types[0] === 'locality')?.long_name ?? '',
            { shouldDirty: true, shouldValidate: true },
        );
    }

    const handleConfirm = () => {
        // Actualizar coordenadas y location primero, independientemente de addresses
        const formattedLat = formatCoordinateNumber(viewState.latitude);
        const formattedLng = formatCoordinateNumber(viewState.longitude);
        const locationText = `Lat: ${formatCoordinate(formattedLat)}, Lng: ${formatCoordinate(formattedLng)}`;

        setValue('latitude', formattedLat, { shouldDirty: true });
        setValue('longitude', formattedLng, { shouldDirty: true });
        setValue('location', locationText, {
            shouldDirty: true,
            shouldValidate: true,
        });

        // Luego actualizar los demás campos si hay addresses
        handleAddressChange();
        
        // Cerrar el diálogo
        setDialogMapState(false);
    };

    // Función de geocodificación
    const searchLocation = useCallback(
        (lat: number, lng: number, updateFormFields: boolean = false) => {
            if (!geocoderRef.current) return;

            geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    const { address_components, formatted_address } = results[0];
                    setAddresses({ formatted_address, address_components: address_components || [] });

                    // Actualizar campos del formulario si se solicita
                    if (updateFormFields) {
                        // Actualizar coordenadas y location
                        const formattedLat = formatCoordinateNumber(lat);
                        const formattedLng = formatCoordinateNumber(lng);
                        const locationText = `Lat: ${formatCoordinate(formattedLat)}, Lng: ${formatCoordinate(formattedLng)}`;

                        setValue('latitude', formattedLat, { shouldDirty: true });
                        setValue('longitude', formattedLng, { shouldDirty: true });
                        setValue('location', locationText, {
                            shouldDirty: true,
                            shouldValidate: true,
                        });

                        setValue(
                            POSTAL_CODE,
                            address_components?.find(({ types }: { types: string[] }) => types[0] === 'postal_code')
                                ?.long_name ?? '',
                            { shouldDirty: true, shouldValidate: true },
                        );
                        setValue(
                            COUNTRY,
                            address_components?.find(({ types }: { types: string[] }) => types[0] === 'country')
                                ?.long_name ?? '',
                            { shouldDirty: true, shouldValidate: true },
                        );
                        setValue(ADDRESS_TEXT, formatted_address ?? '', { shouldDirty: true, shouldValidate: true });
                        setValue(
                            CITY,
                            address_components?.find(({ types }: { types: string[] }) => types[0] === 'locality')
                                ?.long_name ?? '',
                            { shouldDirty: true, shouldValidate: true },
                        );
                    }
                }
            });
        },
        [setValue],
    );

    // Inicializar el flag cuando se abre el diálogo
    useEffect(() => {
        if (dialogMapState && isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
        }
    }, [dialogMapState]);

    // Resetear cuando se cierra el diálogo
    useEffect(() => {
        if (!dialogMapState) {
            isInitialLoadRef.current = true;
            mapRef.current = null;
            geocoderRef.current = null;
        }
    }, [dialogMapState]);

    // Actualizar marcador cuando cambia viewState desde PlacesAutocomplete (sin recargar)
    // No actualizar si el cambio viene del arrastre del marcador
    useEffect(() => {
        if (mapRef.current && !isInitialLoadRef.current && !isDraggingRef.current) {
            mapRef.current.panTo({ lat: viewState.latitude, lng: viewState.longitude });
            searchLocation(viewState.latitude, viewState.longitude, true);
        }
    }, [viewState.latitude, viewState.longitude, searchLocation]);

    const onMapLoad = useCallback(
        (map: google.maps.Map) => {
            mapRef.current = map;
            geocoderRef.current = new google.maps.Geocoder();
            // Geocodificación inicial
            searchLocation(viewState.latitude, viewState.longitude, true);
        },
        [viewState.latitude, viewState.longitude, searchLocation],
    );

    const onMapUnmount = useCallback(() => {
        mapRef.current = null;
    }, []);

    const onMapClick = useCallback(
        (e: google.maps.MapMouseEvent) => {
            if (!e.latLng) return;

            const clickLat = e.latLng.lat();
            const clickLng = e.latLng.lng();

            // Centrar el mapa en la posición del click
            if (mapRef.current) {
                mapRef.current.panTo({ lat: clickLat, lng: clickLng });
            }

            // Actualizar viewState
            setViewState((old) => ({
                latitude: clickLat,
                longitude: clickLng,
                zoom: old.zoom,
            }));

            // Geocodificar y actualizar campos del formulario
            searchLocation(clickLat, clickLng, true);
        },
        [searchLocation],
    );

    const onMarkerDragStart = useCallback(() => {
        isDraggingRef.current = true;
    }, []);

    const onMarkerDragEnd = useCallback(
        (e: google.maps.MapMouseEvent) => {
            if (!e.latLng) return;

            const newLat = e.latLng.lat();
            const newLng = e.latLng.lng();

            // Actualizar viewState primero (sin que el useEffect interfiera porque isDraggingRef está en true)
            setViewState((old) => ({
                latitude: newLat,
                longitude: newLng,
                zoom: old.zoom,
            }));

            // Centrar el mapa en la nueva posición del marcador
            if (mapRef.current) {
                mapRef.current.panTo({ lat: newLat, lng: newLng });
            }

            // Geocodificar y actualizar campos del formulario
            searchLocation(newLat, newLng, true);

            // Resetear el flag
            isDraggingRef.current = false;
        },
        [searchLocation],
    );

    return (
        <DialogTemplate
            title={translate('site.form.map_title')}
            maxWidth={'lg'}
            open={dialogMapState}
            onClose={() => setDialogMapState(false)}
        >
            <Box
                sx={{
                    display: 'grid',
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                    }}
                >
                    <PlacesAutocomplete setViewState={setViewState} />
                </Box>

                <Box position={'relative'} width={'100%'} height={400}>
                    <LoadScript googleMapsApiKey={GEOCODE_APIKEY ?? ''} libraries={GOOGLE_MAPS_LIBRARIES}>
                        <GoogleMap
                            mapContainerStyle={{ width: '100%', height: '100%' }}
                            center={{ lat: viewState.latitude, lng: viewState.longitude }}
                            zoom={viewState.zoom}
                            onLoad={onMapLoad}
                            onUnmount={onMapUnmount}
                            onClick={onMapClick}
                            options={{
                                disableDefaultUI: false,
                                zoomControl: true,
                                gestureHandling: 'greedy',
                            }}
                        >
                            <Marker
                                position={{ lat: viewState.latitude, lng: viewState.longitude }}
                                draggable={true}
                                onDragStart={onMarkerDragStart}
                                onDragEnd={onMarkerDragEnd}
                            />
                        </GoogleMap>
                    </LoadScript>
                </Box>

                <ButtonsBlock>
                    <Button onClick={() => setDialogMapState(false)}>{translate('ra.action.cancel')}</Button>

                    <Button onClick={handleConfirm} variant={'contained'}>
                        {translate('ra.action.confirm')}
                    </Button>
                </ButtonsBlock>
            </Box>
        </DialogTemplate>
    );
};
